import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Megaphone,
    Loader2,
    Save,
    Trash2,
    Plus,
    Calendar,
    Eye,
    EyeOff
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Announcement {
    id: string;
    message: string;
    message_en: string | null;
    is_active: boolean;
    bg_color: string;
    text_color: string;
    expires_at: string | null;
    created_at: string;
    updated_at: string;
}

const AnnouncementManagement = () => {
    const { toast } = useToast();
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Form state for new/edit announcement
    const [editId, setEditId] = useState<string | null>(null);
    const [message, setMessage] = useState("");
    const [messageEn, setMessageEn] = useState("");
    const [bgColor, setBgColor] = useState("#ef4444");
    const [textColor, setTextColor] = useState("#ffffff");
    const [expiresAt, setExpiresAt] = useState("");
    const [isActive, setIsActive] = useState(false);

    const fetchAnnouncements = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await (supabase as any)
                .from("announcements")
                .select("*")
                .order("updated_at", { ascending: false });

            if (error) throw error;
            setAnnouncements(data || []);
        } catch (err: any) {
            console.error("Error fetching announcements:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const resetForm = () => {
        setEditId(null);
        setMessage("");
        setMessageEn("");
        setBgColor("#ef4444");
        setTextColor("#ffffff");
        setExpiresAt("");
        setIsActive(false);
    };

    const handleEdit = (announcement: Announcement) => {
        setEditId(announcement.id);
        setMessage(announcement.message);
        setMessageEn(announcement.message_en || "");
        setBgColor(announcement.bg_color);
        setTextColor(announcement.text_color);
        setExpiresAt(announcement.expires_at ? announcement.expires_at.slice(0, 16) : "");
        setIsActive(announcement.is_active);
    };

    const handleSave = async () => {
        if (!message.trim()) {
            toast({
                title: "Error",
                description: "กรุณากรอกข้อความประกาศ",
                variant: "destructive",
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                message: message.trim(),
                message_en: messageEn.trim() || null,
                bg_color: bgColor,
                text_color: textColor,
                expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
                is_active: isActive,
            };

            if (editId) {
                // Update existing
                const { error } = await (supabase as any)
                    .from("announcements")
                    .update(payload)
                    .eq("id", editId);

                if (error) throw error;
                toast({ title: "บันทึกสำเร็จ", description: "อัปเดตประกาศเรียบร้อยแล้ว" });
            } else {
                // Create new
                const { error } = await (supabase as any)
                    .from("announcements")
                    .insert(payload);

                if (error) throw error;
                toast({ title: "สร้างสำเร็จ", description: "สร้างประกาศใหม่เรียบร้อยแล้ว" });
            }

            resetForm();
            fetchAnnouncements();
        } catch (err: any) {
            console.error("Error saving announcement:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm("คุณแน่ใจหรือไม่ที่จะลบประกาศนี้?")) return;

        try {
            const { error } = await (supabase as any)
                .from("announcements")
                .delete()
                .eq("id", id);

            if (error) throw error;
            toast({ title: "ลบสำเร็จ", description: "ลบประกาศเรียบร้อยแล้ว" });
            fetchAnnouncements();
            if (editId === id) resetForm();
        } catch (err: any) {
            console.error("Error deleting announcement:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        }
    };

    const handleToggleActive = async (id: string, currentStatus: boolean) => {
        try {
            const { error } = await (supabase as any)
                .from("announcements")
                .update({ is_active: !currentStatus })
                .eq("id", id);

            if (error) throw error;
            toast({
                title: !currentStatus ? "เปิดประกาศ" : "ปิดประกาศ",
                description: !currentStatus ? "ประกาศถูกเปิดแล้ว" : "ประกาศถูกปิดแล้ว",
            });
            fetchAnnouncements();
        } catch (err: any) {
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString("th-TH", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="space-y-6">
            {/* Form Card */}
            <Card variant="glass">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Megaphone className="h-5 w-5" />
                        {editId ? "แก้ไขประกาศ" : "สร้างประกาศใหม่"}
                    </CardTitle>
                    <CardDescription>
                        ประกาศจะแสดงเป็นแถบวิ่งด้านบนของทุกหน้า
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="message">ข้อความ (ภาษาไทย) *</Label>
                            <Textarea
                                id="message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="เช่น: ⚠️ ช่วงนี้ระบบ Sora 2 มีปัญหา อาจสร้างไม่ได้"
                                rows={2}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="messageEn">ข้อความ (English)</Label>
                            <Textarea
                                id="messageEn"
                                value={messageEn}
                                onChange={(e) => setMessageEn(e.target.value)}
                                placeholder="e.g. ⚠️ Sora 2 is currently experiencing issues"
                                rows={2}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="bgColor">สีพื้นหลัง</Label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    id="bgColor"
                                    value={bgColor}
                                    onChange={(e) => setBgColor(e.target.value)}
                                    className="w-10 h-10 rounded cursor-pointer"
                                />
                                <Input
                                    value={bgColor}
                                    onChange={(e) => setBgColor(e.target.value)}
                                    className="flex-1"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="textColor">สีตัวอักษร</Label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    id="textColor"
                                    value={textColor}
                                    onChange={(e) => setTextColor(e.target.value)}
                                    className="w-10 h-10 rounded cursor-pointer"
                                />
                                <Input
                                    value={textColor}
                                    onChange={(e) => setTextColor(e.target.value)}
                                    className="flex-1"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="expiresAt">หมดอายุอัตโนมัติ</Label>
                            <Input
                                type="datetime-local"
                                id="expiresAt"
                                value={expiresAt}
                                onChange={(e) => setExpiresAt(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>สถานะ</Label>
                            <div className="flex items-center gap-2 h-10">
                                <Switch
                                    checked={isActive}
                                    onCheckedChange={setIsActive}
                                />
                                <span className={isActive ? "text-green-500" : "text-muted-foreground"}>
                                    {isActive ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Preview */}
                    {message && (
                        <div className="space-y-2">
                            <Label>ตัวอย่าง</Label>
                            <div
                                className="p-3 rounded-lg overflow-hidden text-center font-medium animate-pulse"
                                style={{ backgroundColor: bgColor, color: textColor }}
                            >
                                📢 {message}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        <Button onClick={handleSave} disabled={isSaving} className="gap-2">
                            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {editId ? "บันทึกการแก้ไข" : "สร้างประกาศ"}
                        </Button>
                        {editId && (
                            <Button variant="outline" onClick={resetForm}>
                                ยกเลิก
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Announcements List */}
            <Card variant="glass">
                <CardHeader>
                    <CardTitle>รายการประกาศทั้งหมด</CardTitle>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : announcements.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>ยังไม่มีประกาศ</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {announcements.map((announcement) => (
                                <div
                                    key={announcement.id}
                                    className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border border-border/50"
                                >
                                    <div className="flex items-start gap-3 flex-1">
                                        <div
                                            className="w-4 h-4 rounded-full shrink-0 mt-1"
                                            style={{ backgroundColor: announcement.bg_color }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{announcement.message}</p>
                                            {announcement.message_en && (
                                                <p className="text-sm text-muted-foreground truncate">{announcement.message_en}</p>
                                            )}
                                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                                <span>อัปเดต: {formatDate(announcement.updated_at)}</span>
                                                {announcement.expires_at && (
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="h-3 w-3" />
                                                        หมดอายุ: {formatDate(announcement.expires_at)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleToggleActive(announcement.id, announcement.is_active)}
                                            className={announcement.is_active ? "text-green-500" : "text-muted-foreground"}
                                        >
                                            {announcement.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleEdit(announcement)}
                                        >
                                            แก้ไข
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(announcement.id)}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AnnouncementManagement;
